import { getRiskCategory } from '@/data/RiskCalculator';
import { Strings } from '@/constants/strings';

const GAUGE_MAX = 40;
const CX = 100;
const CY = 95;
const R = 75;
const NEEDLE_LENGTH = 68;

function getCategoryLabel(cat: 'low' | 'moderate' | 'high' | 'veryHigh'): string {
  switch (cat) {
    case 'low':
      return Strings.riskLow;
    case 'moderate':
      return Strings.riskModerate;
    case 'high':
      return Strings.riskHigh;
    case 'veryHigh':
      return Strings.riskVeryHigh;
  }
}

const categoryTextClasses: Record<'low' | 'moderate' | 'high' | 'veryHigh', string> = {
  low: 'text-risk-low',
  moderate: 'text-risk-moderate',
  high: 'text-risk-high',
  veryHigh: 'text-risk-very-high',
};

/** SVG arc from startAngle to endAngle (degrees, 0 = right, 180 = left), clockwise */
function arcPath(cx: number, cy: number, r: number, startAngleDeg: number, endAngleDeg: number): string {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startAngleDeg));
  const y1 = cy - r * Math.sin(toRad(startAngleDeg));
  const x2 = cx + r * Math.cos(toRad(endAngleDeg));
  const y2 = cy - r * Math.sin(toRad(endAngleDeg));
  const largeArc = Math.abs(endAngleDeg - startAngleDeg) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

/** Needle angle: 0% risk = 180° (left), 40% risk = 0° (right) */
function riskToAngle(riskPercent: number): number {
  const clamped = Math.min(Math.max(riskPercent, 0), GAUGE_MAX);
  return 180 - (clamped / GAUGE_MAX) * 180;
}

interface RiskGaugeProps {
  riskPercent: number;
  /** Reduce vertical margins when embedded in a card layout */
  compact?: boolean;
}

export function RiskGauge({ riskPercent, compact }: RiskGaugeProps) {
  const category = getRiskCategory(riskPercent);
  const needleAngle = riskToAngle(riskPercent);
  const textClass = categoryTextClasses[category];

  // Segment boundaries: Green 180°–135°, Orange 135°–90°, Red 90°–0°
  const pathGreen = arcPath(CX, CY, R, 180, 135);
  const pathOrange = arcPath(CX, CY, R, 135, 90);
  const pathRed = arcPath(CX, CY, R, 90, 0);

  const needleRad = (needleAngle * Math.PI) / 180;
  const needleX = CX + NEEDLE_LENGTH * Math.cos(needleRad);
  const needleY = CY - NEEDLE_LENGTH * Math.sin(needleRad);

  return (
    <div className={`flex flex-col items-center ${compact ? 'my-3' : 'my-6'}`}>
      <div className="relative w-full max-w-[280px]">
        <svg
          viewBox="0 0 200 110"
          className="w-full"
          aria-hidden
        >
          <defs>
            <filter id="gauge-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.15" />
            </filter>
          </defs>
          {/* Green segment (Low Risk) — butt cap so segments meet exactly */}
          <path
            d={pathGreen}
            fill="none"
            stroke="#22C55E"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#gauge-shadow)"
          />
          {/* Orange segment (Moderate–High) */}
          <path
            d={pathOrange}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#gauge-shadow)"
          />
          {/* Red segment (Very High) */}
          <path
            d={pathRed}
            fill="none"
            stroke="#DC2626"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#gauge-shadow)"
          />
          {/* Needle */}
          <line
            x1={CX}
            y1={CY}
            x2={needleX}
            y2={needleY}
            stroke="#0b63b8"
            strokeWidth="3"
            strokeLinecap="round"
          />
          {/* Center pivot circle */}
          <circle cx={CX} cy={CY} r="8" fill="white" stroke="#D1D5DB" strokeWidth="2" />
        </svg>
      </div>
      <div className="mt-1 flex w-full max-w-[280px] justify-between px-2 text-xs text-[#5C5C5C]">
        <span>0%</span>
        <span>{GAUGE_MAX}%</span>
      </div>
      <p className={`mt-3 text-center text-lg font-bold ${textClass}`}>
        {getCategoryLabel(category)}
      </p>
    </div>
  );
}
